import { AbstractControl } from '@angular/forms';
import { ObjectUtils } from 'src/app/shared/utils/object.utils';

export interface ParentControlReference {
  iteration: number;
  path: string[];
}

/**
 * @description
 * reference to `AbstractControl` relative to passed control
 *
 * @string
 * ```typescript
 * return control.parent.get(param);
 * ```
 * @AbstractControl
 * ```typescript
 * return param;
 * ```
 * @ParentControlReference
 * ```typescript
 * // get parent param.iteration times
 * const parent = control.parent.parent...;
 * return parent.get(param.path);
 * ```
 */
export type ControlReference = AbstractControl | ParentControlReference | string[];

export class ControlReferenceHelper {

  static getControlReference(control: AbstractControl, reference: ControlReference): AbstractControl {
    if (!reference) {
      return null;
    }

    if (reference instanceof AbstractControl) {
      return reference;
    } else if (control && control.parent && ObjectUtils.isArray(reference)) {
      return this.getControlParentReference(control, { iteration: 1, path: reference });
    } else if (this.isParentControlReference(reference)) {
      return this.getControlParentReference(control, reference);
    }

    return null;
  }

  static isParentControlReference(value: any): value is ParentControlReference {
    return value && value.iteration && value.path;
  }

  static getControlParentReference(control: AbstractControl, reference: ParentControlReference): AbstractControl {
    try {
      return this.findReference(control, reference);
    } catch (error) {
      return null;
    }
  }

  private static findReference(control: AbstractControl, reference: ParentControlReference): AbstractControl {
    let parent = control;
    for (let i = 0; i < reference.iteration; ++i) {
      parent = parent.parent;
    }

    return reference.path.reduce((parentControl, path) => parentControl.get(path), parent);
  }
}
